/* eslint no-shadow: ["error", { "allow": ["state"] }] */
import Vue from 'vue'
import createAsyncMutation from '@/utils/generators'

// Initial state
const state = {
  status: null,
  error: null,
  allSessionsStatus: null,
  allSessionStatisticsStatus: null,
  mapStatus: null,
  mapMultiplier: 1,
  analyticsCount: 0,
  sessions: [],
  count: 0,
  page: 1,
  session: {
    data: [],
    statistics: {}
  }
}

// Getters
const getters = {
  allSessionsLoading: state => state.allSessionsStatus === 'loading',
  mapLoading: state => state.mapStatus === 'loading',
  mapMultiplier: state => state.mapMultiplier,
  sessionsLoading: state => state.status === 'loading',
  sessionStatisticsLoading: state => state.allSessionStatisticsStatus === 'loading',
  sessionsStatus: state => state.status,
  sessionsError: state => state.error,
  sessions: state => state.sessions,
  session: state => state.session,
  sessionData: state => state.session.data,
  sessionCount: state => state.count,
  sessionPage: state => state.page
}
// Mutations
const mutations = {
  GET_SESSIONS_PENDING(state) {
    // Reset state.sessions so it doesn't fill up endlessly when selecting different vehicles
    state.sessions = []
    state.status = 'loading'
    state.allSessionStatus = 'loading'
    state.allSessionStatisticsStatus = 'loading'
  },
  GET_SESSIONS_SUCCESS(state, { data, count }) {
    state.analyticsCount = 0
    state.error = null
    state.status = 'success'
    state.count = count
    // Sort sessions by datetimeStart
    const sortedSessions = data
      .sort((x, y) => y.datetimeStart - x.datetimeStart)
      .map(session => Object.assign({}, session, { statistics: {} }))
    state.sessions = sortedSessions
  },
  GET_SESSIONS_FAILURE(state, error) {
    state.error = error || 'We failed to load the sessions'
    state.status = 'error'
  },
  CLEAR_SESSION(state) {
    state.session = {
      data: [],
      statistics: {}
    }
  },
  GET_SESSION_PENDING(state) {
    state.status = 'loading'
  },
  GET_SESSION_SUCCESS(state, { data }) {
    state.error = null
    state.status = 'success'
    state.session = Object.assign({}, state.session, data)
    // Add session to sessionsObject if it is not yet there
    const index = state.sessions.findIndex(x => x.sessionid === data.sessionid)
    if (index === -1) state.sessions.push(state.session)
  },
  GET_SESSION_FAILURE(state, error) {
    state.error = error || 'We failed to load the session'
    state.status = 'error'
  },
  SELECT_SESSION(state, { id, checked }) {
    const index = state.sessions.findIndex(session => session.id === id)
    Vue.set(state.sessions, index, Object.assign({}, state.sessions[index], { checked }))
  },
  SELECT_ALL_SESSIONS(state) {
    for (const session of state.sessions) {
      Vue.set(session, 'checked', true)
    }
  },
  GET_SESSION_DATA_PENDING(state) {
    state.status = 'loading'
    state.mapStatus = 'loading'
  },
  GET_SESSION_DATA_SUCCESS(state, { data }) {
    state.status = 'success'
    state.mapStatus = 'success'
    let sampledData = []
    // Limit number of datapoints to 1000
    if (data.length > 1000) {
      const multiplier = data.length / 1000
      state.mapMultiplier = multiplier
      for (const i of Array(1000).keys()) {
        const dataIndex = Math.round(i * multiplier)
        sampledData[i] = Object.freeze(data[dataIndex])
      }
    } else {
      sampledData = data
    }
    state.session.data = Object.freeze(sampledData)
  },
  GET_SESSION_DATA_FAILURE(state) {
    state.status = 'error'
    state.session.data = []
  },
  GET_TINY_SESSION_DATA_PENDING() {
    // TODO: Build in loaders
  },
  GET_TINY_SESSION_DATA_SUCCESS(state, { data, options }) {
    let sampledData = []
    // Limit number of datapoints to 1000
    const index = state.sessions.findIndex(x => x.id === options.params.session)
    if (data.length > 1000) {
      const multiplier = data.length / 1000
      state.mapMultiplier = multiplier
      for (const i of Array(1000).keys()) {
        const dataIndex = Math.round(i * multiplier)
        sampledData[i] = Object.freeze(data[dataIndex])
      }
    } else {
      sampledData = data
    }
    state.sessions[index].map_data = Object.freeze(sampledData)
  },
  GET_TINY_SESSION_DATA_FAILURE(state, options) {
    // TODO: Handle errors
    const index = state.sessions.findIndex(x => x.id === options.params.session)
    state.sessions[index].map_data = 'no_data'
  },
  GET_SESSION_STATISTICS_PENDING(state) {
    state.session.statistics = {}
    state.allSessionStatisticsStatus = 'loading'
  },
  GET_SESSION_STATISTICS_SUCCESS(state, { data, options }) {
    const index = state.sessions.findIndex(x => x.sessionid === options.params.session)
    if (!data) {
      state.sessions[index].state = 'error'
    } else {
      state.sessions[index].state = 'success'
      state.session.statistics = data
      // Remove some decimals
      state.session.statistics.nox_g = (parseFloat(data.nox) / 1000).toFixed(2)
      state.session.statistics.distance_km = parseFloat(data.distance_km).toFixed(1)
      if (state.analyticsCount !== state.sessions.length) {
        state.analyticsCount += 1
        state.sessions[index].statistics = data
        state.sessions[index].statistics.nox_g = (parseFloat(data.nox) / 1000).toFixed(2)
        if (state.analyticsCount === state.sessions.length) {
          state.allSessionsStatus = 'success'
        }
      }
    }

    // Check if this was the last session, than stop loading
    const resolvedStats = state.sessions.filter(x => x.state === 'success' || x.state === 'error').length
    const statsCount = state.sessions.length
    if (resolvedStats === statsCount) state.allSessionStatisticsStatus = 'success'
  },
  GET_SESSION_STATISTICS_FAILURE(state, options) {
    state.allSessionStatisticsStatus = 'success'
    const index = state.sessions.findIndex(x => x.sessionid === options.params.session)
    state.sessions[index].state = 'error'

    // Check if this was the last session, than stop loading
    const resolvedStats = state.sessions.filter(x => x.state === 'success' || x.state === 'error').length
    const statsCount = state.sessions.length
    if (resolvedStats === statsCount) state.allSessionStatisticsStatus = 'success'
  },
  SET_SESSION_PAGE(state, page) {
    state.page = page
  }
}

// Actions
const actions = {
  getSessions: ({ dispatch, rootState }, params) => {
    const url = `${rootState.api.host}/${rootState.api.endpoints.sessions}/`
    const options = { params }
    const mutationTypes = createAsyncMutation('GET_SESSIONS')
    return dispatch('fetch', { url, mutationTypes, options }).then(sessions => sessions)
  },
  getSession: ({ dispatch, commit, rootState }, id) => {
    const url = `${rootState.api.host}/${rootState.api.endpoints.deepSessions}/${id}/`
    const mutationTypes = createAsyncMutation('GET_SESSION')
    commit('CLEAR_SESSION')
    return dispatch('fetch', { url, mutationTypes }).then(session => session)
  },
  getSessionData: ({ dispatch, rootState }, params) => {
    const url = `${rootState.api.host}/${rootState.api.endpoints.map}/`
    const options = { params }
    const mutationTypes = createAsyncMutation('GET_SESSION_DATA')
    return dispatch('fetch', { url, mutationTypes, options }).then(sessionData => sessionData)
  },
  getTinySessionData: ({ dispatch, rootState }, params) => {
    const url = `${rootState.api.host}/${rootState.api.endpoints.map}/`
    const options = { params }
    const mutationTypes = createAsyncMutation('GET_TINY_SESSION_DATA')
    return dispatch('fetch', { url, mutationTypes, options }).then(sessionData => sessionData)
  },
  getSessionStatistics: ({ dispatch, rootState }, params) => {
    const url = `${rootState.api.host}/${rootState.api.endpoints.sessionStatistics}/`
    const options = { params }
    const mutationTypes = createAsyncMutation('GET_SESSION_STATISTICS')
    dispatch('fetch', { url, mutationTypes, options })
  },
  selectSession: ({ commit }, { id, checked }) => {
    commit('SELECT_SESSION', { id, checked })
  },
  selectAllSessions: ({ commit }) => {
    commit('SELECT_ALL_SESSIONS')
  },
  setSessionPage: ({ commit }, { page }) => {
    commit('SET_SESSION_PAGE', page)
  }
}

export default {
  state,
  actions,
  getters,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/sessions.js